import * as React from "react"
import { OrganizationKind } from "@digits-graphql/frontend/graphql-bearer"
import { type DigitsRoute } from "@digits-shared/components/Router/DigitsRoute"
import { SvgChevronDownSolid } from "@digits-shared/components/SVGIcons/solid/ChevronDownSolid.svg"
import { isOrgAccountingFirm } from "@digits-shared/helpers/organizationHelper"
import stringHelper from "@digits-shared/helpers/stringHelper"
import { type PathGenerator } from "@digits-shared/hooks/usePathGenerator"
import useRouter from "@digits-shared/hooks/useRouter"
import useSession from "@digits-shared/hooks/useSession"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import styled from "styled-components"
import { useAffiliationEntities } from "src/frontend/components/OS/Applications/hooks/useAffiliationEntities"
import { BadgeName, useBadgeContext } from "src/frontend/components/Shared/Contexts/BadgeContext"
import {
  CustomDropdown,
  DropdownContents,
  DropdownExpando,
  DropdownHead,
  EmptyMessage,
  EntitiesSearch,
  EntityList,
  PickerContainer,
} from "src/frontend/components/Shared/NavSidebar/Menu/menuStyles"
import {
  AddClientRow,
  BusinessRowItem,
  EntityLink,
  EntityListItem,
  type MenuItemsProps,
  ViewAllRow,
} from "src/frontend/components/Shared/NavSidebar/Menu/RowItems"
import {
  matchesLegalEntity,
  sortByLegalEntityName,
} from "src/frontend/components/Shared/NavSidebar/Menu/sidebarMenuFunctions"
import { SIDEBAR_MENU_SIDE_PADDING } from "src/frontend/components/Shared/NavSidebar/sidebarConstants"
import { useNavSidebarContext } from "src/frontend/components/Shared/NavSidebar/useNavSidebarContext"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import type SessionBusinessOrganization from "src/frontend/session/BusinessOrganization"
import type SessionLegalEntity from "src/frontend/session/LegalEntity"

const FirmClientLinks = styled.div`
  width: 100%;
  padding: 0 ${SIDEBAR_MENU_SIDE_PADDING}px;
`
const OrgSelectChevron = styled(SvgChevronDownSolid)`
  transition: transform 250ms ease-in-out;

  &.org-list-open {
    transform: rotate(-180deg);
  }
`

/*
  COMPONENTS
*/

export const AccountantItems: React.FC<MenuItemsProps> = ({ onItemClick }) => {
  const { currentLegalEntity, currentOrganization } = useSession<FrontendSession>()
  const { affiliationEntities } = useAffiliationEntities(currentOrganization?.id)

  // NOTE: Since we show the AccountantItems any time a user has
  // *any* affiliations we must include clients *and* legal entities
  const allLegalEntities = React.useMemo(
    () => [
      ...(currentOrganization?.legalEntities.map((legalEntity) => ({
        legalEntity,
        organization: currentOrganization,
      })) || []),
      ...affiliationEntities,
    ],
    [affiliationEntities, currentOrganization]
  )

  const [searchTerm, setSearchTerm] = React.useState("")
  const filteredLegalEntities = React.useMemo(() => {
    const searchTermRegex = new RegExp(stringHelper.escapeRegExp(searchTerm), "i")
    const filtered = searchTerm
      ? allLegalEntities.filter(({ legalEntity }) =>
          matchesLegalEntity(legalEntity, searchTermRegex)
        )
      : allLegalEntities

    return sortByLegalEntityName(filtered)
  }, [allLegalEntities, searchTerm])

  if (!currentOrganization) return null

  const isAccountingFirm = isOrgAccountingFirm(currentOrganization)
  const showSearch = allLegalEntities.length > 5
  const hideList =
    !allLegalEntities.length ||
    // NOTE: when an operator org is shown alongside the accounting firms it is likely
    // to only have a single LE since we do not currently support adding multiple.
    // In this case we should hide the list of LEs since the information is redundant
    (allLegalEntities.length === 1 && currentOrganization.kind === OrganizationKind.Operator)

  return (
    <>
      {isAccountingFirm && (
        <FirmClientLinks>
          <ViewAllRow org={currentOrganization} onItemClick={onItemClick} />
        </FirmClientLinks>
      )}
      {showSearch && (
        <EntitiesSearch
          onChange={setSearchTerm}
          onSubmit={setSearchTerm}
          onClear={setSearchTerm}
          initialValue={searchTerm}
          shouldDebounceOnChange
          placeholder="Find a client"
          stopClickProp
        />
      )}
      {!hideList && (
        <EntityList>
          {filteredLegalEntities.map((client) => (
            <EntityListItem
              key={client.legalEntity.id}
              isSelected={currentLegalEntity?.id === client.legalEntity.id}
              organization={client.organization}
              legalEntity={client.legalEntity}
              onItemClick={onItemClick}
            />
          ))}

          {showSearch && !filteredLegalEntities.length && (
            <EmptyMessage>No clients found</EmptyMessage>
          )}
        </EntityList>
      )}
      {isAccountingFirm && <AddClientRow onItemClick={onItemClick} />}
    </>
  )
}

export const AccountingOrgsList: React.FC = () => {
  const { organizations, currentOrganization } = useSession<FrontendSession>()
  const { value: showOrgs, toggle } = useStateBoolean()
  const { hideHeaderMenu } = useNavSidebarContext()

  if (!currentOrganization) {
    return null
  }

  const selectableOrganizations = organizations.filter(
    (org) =>
      org.id !== currentOrganization.id &&
      (org.affiliatedLegalEntities.length > 0 ||
        org.legalEntities.length > 0 ||
        isOrgAccountingFirm(org))
  )

  return (
    <PickerContainer>
      <CustomDropdown>
        <DropdownExpando
          className={stringHelper.classNames(showOrgs && "expanded")}
          disabled={!selectableOrganizations.length}
        >
          <DropdownHead onClick={toggle}>
            <BusinessRowItem
              name={currentOrganization.name}
              iconUrl={currentOrganization.iconUrl}
            />
            {selectableOrganizations.length > 0 && (
              <OrgSelectChevron className={stringHelper.classNames(showOrgs && "org-list-open")} />
            )}
          </DropdownHead>

          {/* 
          DropdownContents *cannot* be rendered within a portal, or else the useClickAway within SidebarHeader
          will register the click as external to the menu and prevent propagation to the link,
          closing the menu without navigating.
        */}
          {showOrgs && (
            <DropdownContents onClick={hideHeaderMenu}>
              {selectableOrganizations.map((org) => (
                <OrgListItem
                  key={org.id}
                  isSelected={currentOrganization.id === org.id}
                  organization={org}
                />
              ))}
            </DropdownContents>
          )}
        </DropdownExpando>
      </CustomDropdown>
    </PickerContainer>
  )
}

const OrgListItem: React.FC<{
  isSelected: boolean
  organization: SessionBusinessOrganization
}> = ({ isSelected, organization }) => {
  const pathGenerator = useFrontendPathGenerator()
  const { location } = useRouter()
  const currentRoute = routes[location.name] ?? routes.legalEntityHome

  const { ownLegalEntity, preferredLegalEntity } = React.useMemo(() => {
    const { name, slug, legalEntities, affiliations } = organization

    // NOTE: Do not filter own LEs on view versions, as Pay-only users won't have view versions
    // but still need to be able to use the org picker
    const ownLegalEntities = legalEntities.filter((le) => le.hasDashboardAccess())
    const affiliateLegalEntities = (
      affiliations?.filter(
        (aff) => aff.entity.accountingViewVersions.size && aff.entity.hasDashboardAccess()
      ) ?? []
    ).map((a) => a.entity)

    // Prefer the "active" Entities with same name or slug, otherwise just the first one from the list of active LEs
    const matchNameOrSlug = (le: SessionLegalEntity) =>
      !!le.name.match(new RegExp(name, "i")) || !!le.slug.match(new RegExp(slug, "i"))

    const ownLegalEntity = ownLegalEntities.find(matchNameOrSlug) ?? ownLegalEntities[0]
    const affiliateLegalEntity =
      affiliateLegalEntities.find(matchNameOrSlug) ?? affiliateLegalEntities[0]

    const preferredLegalEntity = ownLegalEntity ?? affiliateLegalEntity

    return { preferredLegalEntity, ownLegalEntity }
  }, [organization])

  const { badgeCount } = useBadgeContext()
  const { iconUrl, name } = organization
  const actionItemsCount = badgeCount(ownLegalEntity?.id ?? "", BadgeName.ActionItems) || 0

  const linkProperties = getOrgLinkProperties({
    preferredLegalEntity,
    pathGenerator,
    organization,
    currentRoute,
  })

  if (!linkProperties) {
    return null
  }

  const showBadge = !!ownLegalEntity && !linkProperties.pending && actionItemsCount > 0

  return (
    <EntityLink
      to={linkProperties.to}
      className={stringHelper.classNames(isSelected && "selected")}
      // only disable the accountant menu item if the link is for a legal entity (not an org) and that LE is pending
      disabled={linkProperties.pending}
      replace
    >
      <BusinessRowItem
        name={name}
        iconUrl={iconUrl}
        isPending={linkProperties.pending}
        badgeCount={showBadge && actionItemsCount}
      />
    </EntityLink>
  )
}

/**
 * Determine the correct route and features for the provided organization based on
 * the organization kind and whether or not a preferred legal entity was found for the organization
 */
function getOrgLinkProperties({
  pathGenerator,
  currentRoute,
  organization,
  preferredLegalEntity,
}: {
  pathGenerator: PathGenerator
  currentRoute: DigitsRoute
  organization: SessionBusinessOrganization
  preferredLegalEntity: SessionLegalEntity | undefined
}) {
  // if we are on the firm dashboard and we select a different accounting firm remain on the firm dashboard
  if (
    routes.firmDashboardRoot.isRouteOrChildOfRoute(currentRoute) &&
    isOrgAccountingFirm(organization)
  ) {
    return {
      to: pathGenerator(routes.firmDashboardFirmKnown, { orgSlug: organization.slug }),
    }
  }

  // send accounting firms to the firm dashboard if there is no LE to choose
  if (!preferredLegalEntity && isOrgAccountingFirm(organization)) {
    return {
      to: pathGenerator(routes.firmDashboardFirmKnown, { orgSlug: organization.slug }),
    }
  }

  // if no preferredLegalEntity is found we will not show an item for this org
  if (!preferredLegalEntity) {
    return null
  }

  const isLePending =
    !preferredLegalEntity.hasDashboardAccess() ||
    preferredLegalEntity.isPending ||
    preferredLegalEntity.isNew

  // we can only reuse the same route if it includes an leSlug
  // otherwise it will needlessly get added as a query string
  // when we intend it to be a path parameter
  if (currentRoute.parameterizedPath.includes(":leSlug")) {
    return {
      to: pathGenerator(currentRoute, { leSlug: preferredLegalEntity.slug }),
      pending: isLePending,
    }
  }

  // if all else fails send them to the LE home
  return {
    to: pathGenerator(routes.legalEntityHome, { leSlug: preferredLegalEntity.slug }),
    pending: isLePending,
  }
}
